import { ROUTES } from "@/constants/routes";

export type SeoPageConfig = {
  title: string;
  description: string;
  robots?: string;
  jsonLdType?: "website" | "organization" | "medicalWebPage";
};

export const SITE_NAME = "Confida Care";
export const DEFAULT_DESCRIPTION =
  "Find trusted providers, compare options, and book or request appointments with Confida Care.";

export const DEFAULT_OG_IMAGE = "/images/og-default.svg";

export const SEO_BY_ROUTE: Array<{ pattern: string; config: SeoPageConfig }> = [
  {
    pattern: ROUTES.home,
    config: {
      title: "Confida Care | Find Doctors and Book Care",
      description:
        "Search by symptoms or specialty, discover providers near you, and connect to the right care faster.",
      jsonLdType: "website",
    },
  },
  {
    pattern: ROUTES.features,
    config: {
      title: "ConfidaCare Features | AI Patient Access, Provider Search & Healthcare Lead Generation",
      description:
        "Explore ConfidaCare's AI-powered healthcare features including symptom search, provider matching, WhatsApp patient access, appointment requests, lead generation, family booking, and premium provider support.",
      jsonLdType: "medicalWebPage",
    },
  },
  {
    pattern: ROUTES.providerDetail,
    config: {
      title: "Provider Details | Confida Care",
      description:
        "Review provider specialties, location, and booking options to decide your next step in care.",
      jsonLdType: "medicalWebPage",
    },
  },
  {
    pattern: ROUTES.faq,
    config: {
      title: "Frequently Asked Questions | Confida Care",
      description:
        "Get answers about searching providers, booking appointments, request booking, and using Confida Care safely.",
      jsonLdType: "website",
    },
  },
  {
    pattern: ROUTES.privacy,
    config: {
      title: "Privacy Policy | Confida Care",
      description:
        "Learn how Confida Care handles personal data, security practices, and privacy controls.",
      jsonLdType: "organization",
    },
  },
  {
    pattern: ROUTES.results,
    config: {
      title: "Search Results | Confida Care",
      description:
        "Explore relevant providers and care pathways based on your search and symptom context.",
      robots: "noindex,follow",
      jsonLdType: "website",
    },
  },
  {
    pattern: ROUTES.microEntrySpeciality,
    config: {
      title: "Specialty Providers | Confida Care",
      description:
        "Find providers by specialty near you, compare matches, and request care quickly.",
      jsonLdType: "medicalWebPage",
    },
  },
  {
    pattern: ROUTES.microEntrySpecialityWithZip,
    config: {
      title: "Specialty Providers by ZIP | Confida Care",
      description:
        "Find providers by specialty in your area and refine options by distance and availability.",
      jsonLdType: "medicalWebPage",
    },
  },
  {
    pattern: ROUTES.microEntryTaxonomy,
    config: {
      title: "Taxonomy-Based Provider Search | Confida Care",
      description:
        "Search providers using taxonomy categories and explore trusted local care options.",
      jsonLdType: "medicalWebPage",
    },
  },
  {
    pattern: ROUTES.microEntryTaxonomyWithZip,
    config: {
      title: "Taxonomy Providers by ZIP | Confida Care",
      description:
        "Browse taxonomy-matched providers near your ZIP and request care with confidence.",
      jsonLdType: "medicalWebPage",
    },
  },
  {
    pattern: ROUTES.microEntryConversation,
    config: {
      title: "Symptom-Based Care Search | Confida Care",
      description:
        "Start from symptoms and discover relevant provider options and care direction.",
      jsonLdType: "medicalWebPage",
    },
  },
  {
    pattern: ROUTES.microEntryConversationWithZip,
    config: {
      title: "Symptom Care Search by ZIP | Confida Care",
      description:
        "Search symptom-based provider matches near your ZIP with clear next-step guidance.",
      jsonLdType: "medicalWebPage",
    },
  },
  {
    pattern: ROUTES.microEntryHomeRemedy,
    config: {
      title: "Home Remedies | Confida Care",
      description:
        "Explore symptom-based home remedy suggestions and safe self-care guidance from Confida Care.",
      jsonLdType: "medicalWebPage",
    },
  },
];

export const NOINDEX_PREFIXES = ["/user", "/providers", "/admins", "/auth", "/oauth"];

